import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { GmailProfile } from '../hooks/useGmailAuth';

interface ConfigScreenProps {
  profile: GmailProfile | null;
  isSignedIn: boolean;
  loading: boolean;
  authError: string | null;
  onSignIn: () => Promise<GmailProfile | void>;
  onSignOut: () => Promise<void>;
  onStart: (
    senders: string[],
    maxParallel: number,
    dateAfter: string,
    dateBefore: string
  ) => void;
}

/** Internal: max parallel downloads per extraction. Not exposed to user. */
const MAX_PARALLEL_DOWNLOADS = 8;

function parseSendersFile(text: string): string[] {
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    return text
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#'))
      .map(String)
      .filter(Boolean);
  }
}

export const ConfigScreen: React.FC<ConfigScreenProps> = ({
  profile,
  isSignedIn,
  loading,
  authError,
  onSignIn,
  onSignOut,
  onStart,
}) => {
  const [senders, setSenders] = useState<string[]>([]);
  const [dateAfter, setDateAfter] = useState('');
  const [dateBefore, setDateBefore] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmEmailsText, setConfirmEmailsText] = useState('');
  const [dropzoneText, setDropzoneText] = useState(
    'Drop file or click to browse (optional—leave empty for all)'
  );
  const [signingIn, setSigningIn] = useState(false);
  const [authStatus, setAuthStatus] = useState<{ message: string; type: 'error' | 'success' } | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const list = parseSendersFile(reader.result as string);
        setSenders(list);
        setDropzoneText(`${file.name} (${list.length} senders)`);
      };
      reader.readAsText(file);
    },
    []
  );

  const handleDropzoneClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('ge-drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('ge-drag-over');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('ge-drag-over');
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleSignIn = async () => {
    setSigningIn(true);
    setAuthStatus(null);
    try {
      await onSignIn();
    } catch (e) {
      setAuthStatus({
        message: e instanceof Error ? e.message : 'Sign-in failed',
        type: 'error',
      });
    } finally {
      setSigningIn(false);
    }
  };

  const valid = isSignedIn;

  const handleStartClick = () => {
    setConfirmEmailsText(senders.join('\n'));
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    const list = parseSendersFile(confirmEmailsText);
    setShowConfirmModal(false);
    onStart(list, MAX_PARALLEL_DOWNLOADS, dateAfter, dateBefore);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  useEffect(() => {
    if (!showConfirmModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancelConfirm();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showConfirmModal]);

  return (
    <div className="ge-config">
      {isSignedIn && profile ? (
        <div className="ge-user-profile">
          {profile.picture && (
            <img
              src={profile.picture}
              alt=""
              className="ge-user-avatar"
              referrerPolicy="no-referrer"
            />
          )}
          <div className="ge-user-info">
            <span className="ge-user-name">{profile.name || ''}</span>
            <span className="ge-user-email">{profile.email || ''}</span>
          </div>
          <button type="button" className="ge-btn-link" onClick={onSignOut}>
            Sign Out
          </button>
        </div>
      ) : (
        <div className="ge-sign-in-container">
          <button
            type="button"
            className="ge-btn-google"
            onClick={handleSignIn}
            disabled={loading || signingIn}
          >
            <span className="ge-google-icon">
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                  fill="#4285F4"
                />
                <path
                  d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
                  fill="#34A853"
                />
                <path
                  d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
                  fill="#FBBC05"
                />
                <path
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58z"
                  fill="#EA4335"
                />
              </svg>
            </span>
            {signingIn ? 'Signing in...' : 'Sign in with Google'}
          </button>
          {(authError || authStatus) && (
            <div
              className={`ge-status-msg ge-status-${authStatus?.type ?? 'error'}`}
              role="alert"
            >
              {authStatus?.message ?? authError}
            </div>
          )}
        </div>
      )}

      <div className="ge-form-group">
        <label htmlFor="ge-senders">List of email addresses</label>
        <p className="ge-hint">
          Plain text (one email per line). Leave empty to extract all threads.
        </p>
        <div
          className="ge-dropzone"
          onClick={handleDropzoneClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleDropzoneClick()}
        >
          <input
            ref={fileInputRef}
            id="ge-senders"
            type="file"
            accept=".json,.txt,.csv"
            className="ge-file-input"
            onChange={handleFileChange}
          />
          <div className="ge-dropzone-content">
            <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
              <path d="M24 4L14 14h7v14h6V14h7L24 4z" fill="#6366f1" />
              <path d="M8 34v8h32v-8H8z" fill="#6366f1" opacity="0.5" />
            </svg>
            <p>{dropzoneText}</p>
          </div>
        </div>
        {senders.length > 0 && (
          <div className="ge-senders-preview">
            <h4>Senders to extract from:</h4>
            <ul>
              {senders.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {false && (
        <div className="ge-form-group">
          <label htmlFor="ge-date-after">Date range (optional)</label>
          <p className="ge-hint">Gmail format: YYYY/MM/DD. Leave blank for no filter.</p>
          <div className="ge-date-row">
            <input
              id="ge-date-after"
              type="text"
              placeholder="After (e.g. 2024/01/01)"
              value={dateAfter}
              onChange={(e) => setDateAfter(e.target.value.trim())}
              className="ge-date-input"
            />
            <input
              id="ge-date-before"
              type="text"
              placeholder="Before (e.g. 2024/12/31)"
              value={dateBefore}
              onChange={(e) => setDateBefore(e.target.value.trim())}
              className="ge-date-input"
            />
          </div>
        </div>
      )}

      <button
        type="button"
        className="ge-btn-primary ge-btn-large"
        disabled={!valid}
        onClick={handleStartClick}
      >
        Start Extraction
      </button>

      {showConfirmModal && (
        <div className="ge-modal-overlay" onClick={handleCancelConfirm}>
          <div
            className="ge-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="ge-confirm-title"
            aria-modal="true"
          >
            <h3 id="ge-confirm-title" className="ge-modal-title">
              Confirm emails to extract
            </h3>
            <p className="ge-modal-hint">
              Review and edit the list below. Leave empty to extract all threads.
            </p>
            <textarea
              className="ge-modal-textarea"
              value={confirmEmailsText}
              onChange={(e) => setConfirmEmailsText(e.target.value)}
              placeholder={'one@example.com\ntwo@example.com'}
              rows={10}
              spellCheck={false}
            />
            <div className="ge-modal-actions">
              <button
                type="button"
                className="ge-btn-secondary"
                onClick={handleCancelConfirm}
              >
                Cancel
              </button>
              <button type="button" className="ge-btn-primary" onClick={handleConfirm}>
                Confirm & Start
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
